/** XeAgents.ai **/
import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";

interface Call {
  id: string;
  status: string;
  duration: number | null;
  classification: string | null;
  creditsDeducted: number | null;
}

interface LeadFunnelProps {
  totalContacts: number;
  calls: Call[];
}

interface FunnelStage {
  label: string;
  count: number;
  credits: number;
  color: string;
}

export function LeadFunnel({ totalContacts, calls }: LeadFunnelProps) {
  const { t } = useTranslation();

  // Calculate funnel stages
  const connected = calls.filter(c => c.status === 'completed');
  const engaged = calls.filter(c => (c.duration || 0) > 60);
  const qualified = calls.filter(c =>
    c.classification === 'hot' || c.classification === 'warm'
  );
  const hotLeads = calls.filter(c => c.classification === 'hot');

  const stages: FunnelStage[] = [
    {
      label: "Total Contacts",
      count: totalContacts,
      credits: calls.reduce((s, c) => s + (c.creditsDeducted || 0), 0),
      color: "bg-blue-500",
    },
    {
      label: "Connected",
      count: connected.length,
      credits: connected.reduce((s, c) => s + (c.creditsDeducted || 0), 0),
      color: "bg-blue-600",
    },
    {
      label: "Engaged (>1 min)",
      count: engaged.length,
      credits: engaged.reduce((s, c) => s + (c.creditsDeducted || 0), 0),
      color: "bg-blue-700",
    },
    {
      label: "Qualified Leads",
      count: qualified.length,
      credits: qualified.reduce((s, c) => s + (c.creditsDeducted || 0), 0),
      color: "bg-indigo-600",
    },
    {
      label: "Hot Leads",
      count: hotLeads.length,
      credits: hotLeads.reduce((s, c) => s + (c.creditsDeducted || 0), 0),
      color: "bg-red-500",
    },
  ];

  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-6">Lead Conversion Funnel</h3>

      <div className="space-y-3">
        {stages.map((stage, index) => {
          const widthPercent = Math.max((stage.count / maxCount) * 100, 10);
          const percentage = totalContacts > 0
            ? ((stage.count / totalContacts) * 100).toFixed(1)
            : "0";

          return (
            <div key={stage.label} className="flex items-center gap-4">
              <div className="flex-1">
                <div
                  className={`${stage.color} h-12 rounded-r-lg flex items-center justify-between px-4 transition-all duration-300`}
                  style={{ width: `${widthPercent}%` }}
                >
                  <span className="font-medium text-white text-sm truncate">
                    {stage.label}
                  </span>
                  <span className="text-white/90 text-sm font-semibold">
                    {stage.count}
                  </span>
                </div>
              </div>
              <div className="w-24 text-right">
                <span className="text-sm text-muted-foreground">
                  {percentage}%
                </span>
              </div>
              <div className="w-28 text-right">
                <span className="text-sm text-muted-foreground">
                  {stage.credits.toFixed(1)} credits
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Funnel Summary */}
      <div className="mt-6 pt-4 border-t">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {totalContacts > 0
                ? ((connected.length / totalContacts) * 100).toFixed(1)
                : "0"}%
            </div>
            <div className="text-xs text-muted-foreground">Connection Rate</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-indigo-600">
              {connected.length > 0
                ? ((qualified.length / connected.length) * 100).toFixed(1)
                : "0"}%
            </div>
            <div className="text-xs text-muted-foreground">Qualification Rate</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-500">
              {qualified.length > 0
                ? ((hotLeads.length / qualified.length) * 100).toFixed(1)
                : "0"}%
            </div>
            <div className="text-xs text-muted-foreground">Hot Lead Rate</div>
          </div>
        </div>
      </div>
    </Card>
  );
}
